import {formatCoveragePercent} from "../../utils/purchaseCostCoveragePresentation.js";
import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {effectiveReportingRange, isSnapshotPeriodIncomplete, snapshotHasPublishedDays, snapshotRowsForPublishedDays} from "../../utils/reportingSnapshotAvailability.js";
import ReportingInfoTooltip from "../../components/ReportingInfoTooltip.jsx";
import {selectedCentreSpendAttributionStatus} from "./CentreSpendAttributionNotice.jsx";
import ReportingSnapshotStatus from "./ReportingSnapshotStatus.jsx";
import ReportingCostCoverageContext from "../../components/reporting/ReportingCostCoverageContext.jsx";
import ProfitabilityMetricTooltip from "../../components/reporting/ProfitabilityMetricTooltip.jsx";
import {
    ChannelMixColors,
    dividePercent,
    divideRatio,
    ExpandablePanelTitle,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartPercent,
    formatChartNumber,
    formatChartMultiplier,
    formatCurrencyItemTooltip,
    KpiCard,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    SortableHeader,
    TablePagination,
    TableSearch,
} from "./ReportingLab.jsx";
import {productProfitabilityRowAvailable} from "../../utils/reportingFinancialCompleteness.js";
import {productAfterMarketingProfitAvailable, productGoodsMarginAvailable, productMarketingAllocationAvailable, productPurchaseCostQuality, productRowGoodsMarginAvailable, productRowMarketingAllocationAvailable} from "../../utils/reportingProductAllocation.js";
import {formatCzechProductLineCount, selectProfitabilityMetric} from "../../utils/reportingPartialProfitability.js";

function formatProductAxisLabel(value) {
    const label = String(value ?? "");
    const maxLength = 24;

    return label.length > maxLength ? `${label.slice(0, maxLength)}...` : label;
}

function nonNegativeCount(value) {
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
}

export function productPurchaseCoveragePresentation(summary) {
    const unknownRows = nonNegativeCount(summary?.purchaseCostUnknownCount);
    const estimatedRows = nonNegativeCount(summary?.purchaseCostEstimatedCount);
    const unresolvedOrders = nonNegativeCount(summary?.unresolvedPurchaseCostOrderCount);
    const hasQualityData = typeof summary?.purchaseCostComplete === "boolean"
        || unknownRows !== null
        || estimatedRows !== null
        || unresolvedOrders !== null;
    const incomplete = summary?.purchaseCostComplete === false
        || (unknownRows !== null && unknownRows > 0)
        || (unresolvedOrders !== null && unresolvedOrders > 0);
    const complete = summary?.purchaseCostComplete === true && !incomplete;

    return {
        visible: summary?.purchasePrice != null || hasQualityData,
        state: incomplete ? "incomplete" : complete ? "complete" : "unknown",
        unknownRows,
        estimatedRows,
        unresolvedOrders,
    };
}

export function productActionablePurchaseCostCounts(summary, productDataIncomplete = false) {
    if (productDataIncomplete) return {lines: null, products: null};
    return {
        lines: nonNegativeCount(summary?.actionableUnknownLineCount),
        products: nonNegativeCount(summary?.productsWithActionableUnknownPurchaseCost),
    };
}

export function settleProductProfitabilityRequest(request, {isActive, onSuccess, onError, onFinally}) {
    return request
        .then((result) => {
            if (isActive()) onSuccess(result);
        })
        .catch((error) => {
            if (isActive()) onError(error);
        })
        .finally(() => {
            if (isActive()) onFinally();
        });
}

export function productProfitabilityLoadAlert(errorMessage, t) {
    return {
        id: "product-profitability-load-error",
        type: "error",
        showClose: false,
        title: t("google_reporting.load_error_title", {defaultValue: "Přehled se nepodařilo načíst"}),
        content: errorMessage || t("google_reporting.load_error", {defaultValue: "Data nejsou započítaná jako nula. Zkuste přehled načíst znovu."}),
    };
}

export function productProfitabilityLoadPresentation({error, errorMessage, t}) {
    return {
        showReport: error !== true,
        alert: error === true ? productProfitabilityLoadAlert(errorMessage, t) : null,
    };
}

export function productProfitabilityPublicationAvailability(productPublication, marketingSnapshot) {
    const productDataComplete = !isSnapshotPeriodIncomplete(productPublication);
    const marketingDataComplete = !isSnapshotPeriodIncomplete(marketingSnapshot);
    const productSummaryAvailable = snapshotHasPublishedDays(productPublication);
    const marketingSummaryAvailable = snapshotHasPublishedDays(marketingSnapshot);

    return {
        productDataComplete,
        marketingDataComplete,
        productSummaryAvailable,
        marketingSummaryAvailable,
        productProfitAvailable: productSummaryAvailable && marketingSummaryAvailable,
    };
}

/** Builds presentation metadata without changing the raw product-publication receipt. */
export function productProfitabilityPublicationForDisplay(productPublication, reportSnapshot) {
    if (!productPublication) return productPublication;
    return {
        ...productPublication,
        reportingExcludedDays: reportSnapshot?.reportingExcludedDays ?? productPublication.reportingExcludedDays ?? [],
    };
}

export function productProfitabilityColumnAvailability(publication, summary, totalItems, products = []) {
    const hasProductRows = Number(totalItems ?? 0) > 0;
    const hasReturnedGrossProfit = products.some((product) => product?.grossMargin !== undefined || product?.knownGrossProfit !== undefined);

    return {
        grossMargin: publication.productSummaryAvailable && hasProductRows && (summary?.grossMargin !== undefined || hasReturnedGrossProfit),
        afterMarketing: publication.productProfitAvailable && hasProductRows && summary?.profitAfterMarketing !== undefined,
    };
}

export function productProfitabilityPurchaseColumnAvailable(publication, summary, products = []) {
    return publication.productSummaryAvailable
        && (summary?.purchasePrice !== undefined || products.some((product) => product?.purchasePrice !== undefined || product?.knownPurchaseCost !== undefined));
}

export function productProfitabilityKnownCostMetrics(summary) {
    const partialProfitability = summary?.partialProfitability;
    const knownLineCount = partialProfitability?.knownLineCount;
    const emptyKnownCohort = knownLineCount !== null && knownLineCount !== undefined && knownLineCount !== "" && Number.isFinite(Number(knownLineCount)) && Number(knownLineCount) === 0;
    return {
        grossProfit: selectProfitabilityMetric({
            fullValue: emptyKnownCohort && summary?.purchaseCostComplete !== true ? undefined : summary?.grossMargin,
            fullComplete: summary?.purchaseCostComplete === true && summary?.grossMargin != null,
            partialProfitability: emptyKnownCohort ? undefined : partialProfitability,
            partialField: "knownGrossProfit",
        }),
        purchaseCost: selectProfitabilityMetric({
            fullValue: emptyKnownCohort && summary?.purchaseCostComplete !== true ? undefined : summary?.purchasePrice,
            fullComplete: summary?.purchaseCostComplete === true && summary?.purchasePrice != null,
            partialProfitability: emptyKnownCohort ? undefined : partialProfitability,
            partialField: "knownPurchaseCost",
        }),
    };
}

export function productProfitabilityWholeReportSelection({summary, snapshotComplete, standardValue, standardComplete, partialField}) {
    return selectProfitabilityMetric({
        fullValue: standardValue,
        fullComplete: standardComplete,
        partialProfitability: snapshotComplete === true ? summary?.partialProfitability : undefined,
        partialField,
    });
}

export function productProfitabilityPurchaseCoveragePercent(partialProfitability) {
    const value = partialProfitability?.revenueCoveragePercent;
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function productProfitabilityRowKnownCostMetrics(product) {
    const knownLineCount = nonNegativeCount(product?.knownLineCount);
    const hasKnownLines = product?.partialAvailable === true && knownLineCount > 0;
    const estimatedLineCount = nonNegativeCount(product?.estimatedLineCount) ?? 0;
    const partial = (field) => hasKnownLines && product?.[field] != null
        ? {value: product[field], partial: true, estimated: estimatedLineCount > 0}
        : {value: null, partial: false, estimated: false};
    const full = (fullField, partialField) => product?.purchaseCostComplete === true && product?.[fullField] != null
        ? {value: product[fullField], partial: false, estimated: estimatedLineCount > 0}
        : partial(partialField);

    return {
        purchaseCost: full("purchasePrice", "knownPurchaseCost"),
        grossProfit: full("grossMargin", "knownGrossProfit"),
        knownLineCount,
        unknownLineCount: nonNegativeCount(product?.unknownLineCount),
        estimatedLineCount,
        showPartialContext: hasKnownLines && product?.partialComplete !== true,
    };
}

export function productRowsNeedPurchaseCostContext(products) {
    return (products ?? []).some((product) => productProfitabilityRowKnownCostMetrics(product).showPartialContext);
}

export function productProfitabilityPartialCostTooltipColumn(showContext, hasPurchasePrice, hasGrossProfit) {
    if (!showContext) {
        return null;
    }

    if (hasPurchasePrice) {
        return "purchasePrice";
    }

    return hasGrossProfit ? "grossMargin" : null;
}

export function productProfitabilityUnallocatedAdjustmentValue(value, productDataIncomplete) {
    return !productDataIncomplete && value != null ? value : null;
}

export function productProfitabilityInitialSort() {
    return {key: "amount", direction: "desc"};
}

export function productProfitabilityListCounts(data, products) {
    const sourceTotalItems = Number(data?.sourceTotalItems ?? products.length);
    const matchingItems = Number(data?.totalItems ?? products.length);
    const loadedItems = Number(data?.loadedItems ?? products.length);

    return {
        sourceTotalItems,
        matchingItems,
        loadedItems,
        isTruncated: data?.isTruncated === true,
    };
}

export function productPurchaseCostCompleteness(summary) {
    return typeof summary?.purchaseCostComplete === "boolean" ? summary.purchaseCostComplete : undefined;
}

export function formatProductPurchaseCoveragePercent(value) {
    const formatted = formatCoveragePercent(value);
    return formatted === null ? null : `${formatted} %`;
}

export function productProfitabilityCentreSpendStatus(attribution, selectedCentreIds = []) {
    if (!Array.isArray(selectedCentreIds) || selectedCentreIds.length === 0) {
        return null;
    }

    return selectedCentreSpendAttributionStatus(attribution, true);
}

export function productProfitabilityAdMetricAvailability(publication, summary, centreSpendStatus = null) {
    const complete = publication.productProfitAvailable && centreSpendStatus === null;
    return {
        complete,
        showKnownAllocation: publication.productSummaryAvailable && summary?.allocatedMarketingSpend !== undefined && summary?.allocatedMarketingSpend !== null,
        profitFieldReturned: summary?.profitAfterMarketing !== undefined,
        poasFieldsReturned: summary?.grossMargin !== undefined && summary?.allocatedMarketingSpend !== undefined,
        showUnavailableAfterMarketing: publication.productSummaryAvailable && centreSpendStatus !== null && summary?.profitAfterMarketing !== undefined,
        showUnavailablePoas: publication.productSummaryAvailable && centreSpendStatus !== null && summary?.grossMargin !== undefined && summary?.allocatedMarketingSpend !== undefined,
        afterMarketing: complete && summary?.profitAfterMarketing !== undefined && productAfterMarketingProfitAvailable(summary),
        allocation: complete && productMarketingAllocationAvailable(summary),
    };
}

export function productProfitabilityAfterMarketingUnavailable(summary) {
    return summary?.marketingAllocationComplete === false;
}

export function productRowMarketingAllocationDisplayAvailable(publication, summary, product, centreSpendStatus = null) {
    if (!publication.productSummaryAvailable || product?.allocatedMarketingSpend == null) {
        return false;
    }

    return centreSpendStatus !== null || productRowMarketingAllocationAvailable(summary, product);
}

export function productGrossProfitChartRows(products) {
    return (products ?? []).filter((product) => productRowGoodsMarginAvailable(product));
}

function productCentreSpendStatusHelp(status, t) {
    if (status === "incomplete") {
        return t("reporting_lab.centre_spend_attribution.channel_partial_help", {
            defaultValue: "Známé hodnoty přiřazené vybraným střediskům; další reklamní náklady nemají jednoznačné středisko. Úplný PNO a zisk po reklamě proto nelze určit.",
        });
    }

    return t("reporting_lab.centre_spend_attribution.channel_unknown_help", {
        defaultValue: "Odpověď neobsahuje údaje o úplnosti přiřazení. Hodnoty mohou být částečné; další reklamní náklady nemají jednoznačné středisko. Úplný PNO a zisk po reklamě proto nelze určit.",
    });
}

export function productCentreSpendKpiPresentation(status, hasValue, t) {
    return {
        meta: hasValue ? t("reporting_lab.metric_basis.business.short_label", {defaultValue: "Dokončené"}) : undefined,
        detail: status == null ? null : productCentreSpendStatusHelp(status, t),
    };
}

export function ProductGrossMarginCell({product, renderCurrencyValue}) {
    const metric = productProfitabilityRowKnownCostMetrics(product).grossProfit;
    const available = metric.value != null;
    return <td className={`py-3 pr-4 text-right ${available && moneyAmount(metric.value) < 0 ? "text-datixo-negative" : "text-slate-600 dark:text-slate-300"}`}>{available ? renderCurrencyValue(metric.value, true) : "—"}</td>;
}

export function productProfitabilityTablePresentation({loading, totalItems, sourceTotalItems, productPublication}) {
    if (loading) {
        return {showPagination: false, showScopeSummary: false, emptyState: null};
    }

    const productDataUnavailable = !snapshotHasPublishedDays(productPublication);
    const publishedSubsetEmpty = isSnapshotPeriodIncomplete(productPublication) && !productDataUnavailable && totalItems === 0;

    return {
        showPagination: !productDataUnavailable,
        showScopeSummary: snapshotHasPublishedDays(productPublication),
        emptyState: productDataUnavailable
            ? "unavailable"
            : publishedSubsetEmpty ? "publishedSubsetEmpty"
                : totalItems > 0 ? null : sourceTotalItems > 0 ? "noMatchingProducts" : "periodEmpty",
    };
}

export const productProfitabilityKpiGridItemClass = "col-span-12 md:col-span-6 xl:col-span-3";

export default function ReportingProductProfitabilityLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);
    const [loadError, setLoadError] = useState(false);
    const [loadErrorMessage, setLoadErrorMessage] = useState(null);
    const [tablePage, setTablePage] = useState(1);
    const [tablePageSize, setTablePageSize] = useState(25);
    const [tableSearch, setTableSearch] = useState("");
    const [debouncedTableSearch, setDebouncedTableSearch] = useState("");
    const [tableSort, setTableSort] = useState(productProfitabilityInitialSort);
    const tableExpandedStorageKey = `reporting-lab:product-profitability-table-expanded:${project?.id ?? "unknown"}`;
    const [tableExpanded, setTableExpanded] = useState(() => typeof window !== "undefined" && window.sessionStorage.getItem(tableExpandedStorageKey) === "1");

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const purchaseCoverageRange = effectiveReportingRange(data?.snapshot, sinceString, untilString);
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const productProfitabilityFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
        limit: tablePageSize,
        page: tablePage,
        search: debouncedTableSearch || null,
        mode: "net",
        sortBy: tableSort.key,
        orderDirection: tableSort.direction,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey, tablePageSize, tablePage, debouncedTableSearch, tableSort]);

    useEffect(() => {
        const timeout = window.setTimeout(() => setDebouncedTableSearch(tableSearch.trim()), 300);
        return () => window.clearTimeout(timeout);
    }, [tableSearch]);

    useEffect(() => {
        window.sessionStorage.setItem(tableExpandedStorageKey, tableExpanded ? "1" : "0");
    }, [tableExpanded, tableExpandedStorageKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setData(null);
        setLoadError(false);
        setLoadErrorMessage(null);

        const erpApi = new ErpCalculationApi(getAuthorityConfiguration());

        const request = erpApi.productProfitability({
            projectId: project.id,
            productProfitabilityFilter,
        });

        settleProductProfitabilityRequest(request, {
            isActive: () => active,
            onSuccess: setData,
            onError: (error) => {
                setLoadError(true);
                et(error).then((message) => { if (active && message) setLoadErrorMessage(message); });
            },
            onFinally: () => setLoading(false),
        });

        return () => {
            active = false;
        };
    }, [et, productProfitabilityFilter, project?.id]);

    const rawProductPublication = data?.productPublication;
    const productPublication = productProfitabilityPublicationForDisplay(rawProductPublication, data?.snapshot);
    const baseProfitabilityPublication = productProfitabilityPublicationAvailability(productPublication, data?.snapshot);
    const centreSpendStatus = productProfitabilityCentreSpendStatus(data?.centreSpendAttribution, centresAllowListed);
    const profitabilityPublication = {
        ...baseProfitabilityPublication,
        productProfitAvailable: baseProfitabilityPublication.productProfitAvailable && centreSpendStatus === null,
    };
    const productDataIncomplete = !profitabilityPublication.productSummaryAvailable;
    const products = snapshotRowsForPublishedDays(data?.products ?? [], productPublication);
    const summary = data?.summary ?? {};
    const partialProfitability = summary.partialProfitability;
    const purchaseCoveragePercent = productProfitabilityPurchaseCoveragePercent(partialProfitability);
    const hasPurchasePrice = productProfitabilityPurchaseColumnAvailable(profitabilityPublication, summary, products);
    const adMetricAvailability = productProfitabilityAdMetricAvailability(baseProfitabilityPublication, summary, centreSpendStatus);
    const hasProfit = adMetricAvailability.afterMarketing;
    const afterMarketingKpiPresentation = productCentreSpendKpiPresentation(centreSpendStatus, hasProfit, t);
    const poasKpiPresentation = productCentreSpendKpiPresentation(centreSpendStatus, !adMetricAvailability.showUnavailablePoas, t);
    const hasGoodsMargin = profitabilityPublication.productSummaryAvailable && productGoodsMarginAvailable(summary);
    const knownCostMetrics = productProfitabilityKnownCostMetrics(summary);
    const grossProfitSelection = knownCostMetrics.grossProfit;
    const purchaseCostSelection = knownCostMetrics.purchaseCost;
    const hasGrossProfitMetric = profitabilityPublication.productSummaryAvailable && grossProfitSelection.allowed && grossProfitSelection.value != null;
    const hasPurchaseCostMetric = profitabilityPublication.productSummaryAvailable && purchaseCostSelection.allowed && purchaseCostSelection.value != null;
    const hasMarketingAllocation = adMetricAvailability.allocation;
    const productPoas = hasGoodsMargin && hasMarketingAllocation ? divideRatio(moneyAmount(summary.grossMargin), moneyAmount(summary.allocatedMarketingSpend)) : null;
    const reportProfitSelection = productProfitabilityWholeReportSelection({
        summary,
        snapshotComplete: baseProfitabilityPublication.productProfitAvailable,
        standardValue: hasProfit ? summary.profitAfterMarketing : undefined,
        standardComplete: hasProfit,
        partialField: "knownProfitAfterAllMarketing",
    });
    const reportProfitFallback = !hasProfit && reportProfitSelection.value !== null && reportProfitSelection.value !== undefined;
    const reportPoasSelection = productProfitabilityWholeReportSelection({
        summary,
        snapshotComplete: baseProfitabilityPublication.productProfitAvailable,
        standardValue: productPoas,
        standardComplete: productPoas !== null,
        partialField: "knownPoas",
    });
    const reportPoasFallback = productPoas === null && reportPoasSelection.value !== null && reportPoasSelection.value !== undefined;
    const showMarketingAllocationCard = hasMarketingAllocation || (centreSpendStatus !== null
        && baseProfitabilityPublication.productSummaryAvailable
        && summary.allocatedMarketingSpend !== undefined);
    const showProfitCard = hasProfit || reportProfitFallback || adMetricAvailability.showUnavailableAfterMarketing;
    const currency = summary.profitAfterMarketing?.currency ?? reportProfitSelection.value?.currency ?? products.find((product) => product.turnover?.currency)?.turnover?.currency ?? projectCurrency(project);
    const totalTurnover = moneyAmount(summary.turnover);
    const totalPurchase = moneyAmount(summary.purchasePrice);
    const totalMarginBeforeMarketing = moneyAmount(summary.grossMargin);
    const totalAllocatedMarketing = moneyAmount(summary.allocatedMarketingSpend);
    const totalProfitAfterMarketing = moneyAmount(hasProfit ? summary.profitAfterMarketing : reportProfitSelection.value);
    const actionablePurchaseCostCounts = productActionablePurchaseCostCounts(summary, productDataIncomplete);
    const {sourceTotalItems, matchingItems, loadedItems, isTruncated} = productProfitabilityListCounts(data, products);
    const tablePresentation = productProfitabilityTablePresentation({
        loading,
        totalItems: productDataIncomplete && !snapshotHasPublishedDays(productPublication) ? 0 : matchingItems,
        sourceTotalItems,
        productPublication,
    });
    const profitabilityColumns = productProfitabilityColumnAvailability(profitabilityPublication, summary, matchingItems, products);
    const purchaseCoveragePresentation = productPurchaseCoveragePresentation(productDataIncomplete ? {} : summary);
    const costQuality = productPurchaseCostQuality(summary);
    const costComplete = costQuality.complete;
    const unallocatedAdjustments = summary.unallocatedAdjustmentsNet;
    const availableUnallocatedAdjustments = productProfitabilityUnallocatedAdjustmentValue(unallocatedAdjustments, productDataIncomplete);
    const adjustmentComplete = !productDataIncomplete && summary.unallocatedAdjustmentComplete === true && unallocatedAdjustments != null;
    const excludedNonProductAmount = summary.excludedNonProductAmount;
    const excludedNonProductOrderCount = Number(summary.excludedNonProductOrderCount ?? 0);
    const hasExcludedNonProductAmounts = !productDataIncomplete
        && (excludedNonProductOrderCount > 0 || (excludedNonProductAmount != null && moneyAmount(excludedNonProductAmount) !== 0));
    const hasUnallocatedAdjustmentNotice = !productDataIncomplete && (
        summary.unallocatedAdjustmentComplete === false
        || Number(summary.unallocatedAdjustmentUnknownOrderCount ?? 0) > 0
        || (unallocatedAdjustments != null && moneyAmount(unallocatedAdjustments) !== 0)
    );
    const marketingMetricsUnavailable = productProfitabilityAfterMarketingUnavailable(summary);
    const centreSpendAttributionIncomplete = data?.centreSpendAttribution?.complete === false;
    const costCoverageAllocationHelp = centreSpendAttributionIncomplete
        ? t("reporting_lab.product_profitability.centre_attribution_incomplete", {defaultValue: "Část reklamních nákladů není přiřazena ke střediskům. Proto nelze vypočítat ukazatele po reklamě pro jednotlivé produkty."})
        : marketingMetricsUnavailable
            ? t("reporting_lab.product_profitability.marketing_allocation_incomplete", {defaultValue: "Reklamní náklady nejsou úplné nebo je nelze přiřadit k produktům. Ukazatele po reklamě proto nejsou dostupné."})
        : null;

    const topProducts = data?.topProductsByProfit ?? [];
    const topFive = hasProfit ? topProducts.filter((product) => product.profitAfterMarketing != null && moneyAmount(product.profitAfterMarketing) > 0).slice(0, 5) : [];
    const productsSortedByAmount = data?.topProductsByAmount ?? [];
    const grossProfitProducts = productGrossProfitChartRows(productsSortedByAmount);
    const quantityChartHeight = Math.max(320, productsSortedByAmount.length * 40 + 64);
    const tablePages = Math.max(1, Number(data?.totalPages ?? 1));
    const normalizedTablePage = Math.min(tablePage, tablePages);
    const visibleProducts = products;
    const showProductCostContext = productRowsNeedPurchaseCostContext(visibleProducts);
    const partialCostTooltipColumn = productProfitabilityPartialCostTooltipColumn(showProductCostContext, hasPurchasePrice, profitabilityColumns.grossMargin);
    const toggleSort = (key) => {
        setTableSort((current) => current.key === key
            ? {key, direction: current.direction === "asc" ? "desc" : "asc"}
            : {key, direction: "desc"});
        setTablePage(1);
    };

    useEffect(() => {
        setTablePage(1);
    }, [sinceString, untilString, centreFilterKey, domainFilterKey]);

    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const profitByProductOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 8, bottom: 12, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        xAxis: {
            type: "category",
            data: topProducts.map((product) => product.productName),
            axisLabel: {fontSize: 12, interval: 0, rotate: 68, width: 210, overflow: "truncate", margin: 14, align: "right", formatter: formatProductAxisLabel},
        },
        yAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: (value) => formatChartCurrency(value, currency)},
        },
        series: [
            {
                name: t("reporting_lab.series.profit", {defaultValue: "Hrubý zisk po reklamě"}),
                type: "bar",
                data: topProducts.map((product, index) => ({
                    value: moneyToMajor(product.profitAfterMarketing),
                    itemStyle: {color: ChannelMixColors[index % ChannelMixColors.length]},
                })),
                barMaxWidth: 28,
            },
        ],
    };

    const grossProfitByProductOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 8, bottom: 12, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        xAxis: {
            type: "category",
            data: grossProfitProducts.map((product) => product.productName),
            axisLabel: {fontSize: 12, interval: 0, rotate: 68, width: 210, overflow: "truncate", margin: 14, align: "right", formatter: formatProductAxisLabel},
        },
        yAxis: {type: "value", axisLabel: {fontSize: 12, formatter: (value) => formatChartCurrency(value, currency)}},
        series: [{
            name: t("reporting_lab.product_profitability.margin_before_marketing_short", {defaultValue: "Hrubý zisk"}),
            type: "bar",
            data: grossProfitProducts.map((product, index) => ({
                value: moneyToMajor(product.grossMargin),
                itemStyle: {color: ChannelMixColors[index % ChannelMixColors.length]},
            })),
            barMaxWidth: 28,
        }],
    };

    const profitByChannelOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        tooltip: {trigger: "item", formatter: (params) => formatCurrencyItemTooltip(params, currency)},
        legend: {bottom: 8, ...chartLegendStyle},
        series: [
            {
                type: "pie",
                radius: ["38%", "58%"],
                center: ["50%", "32%"],
                label: {show: true, formatter: (params) => formatChartPercent(params.percent), ...chartTextStyle},
                labelLine: {show: true, length: 12, length2: 8},
                data: topFive.map((product) => ({
                    name: product.productName,
                    value: moneyToMajor(product.profitAfterMarketing),
                })),
            },
        ],
    };

    const quantityByProductOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartNumber(value)),
        },
        grid: {left: 18, right: 24, top: 18, bottom: 20, containLabel: true},
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: (value) => formatChartNumber(value)},
        },
        yAxis: {
            type: "category",
            inverse: true,
            data: productsSortedByAmount.map((product) => product.productName),
            axisLabel: {fontSize: 12, width: 230, overflow: "truncate", margin: 12},
        },
        series: [
            {
                name: t("reporting_lab.products.quantity", {defaultValue: "Kusy"}),
                type: "bar",
                data: productsSortedByAmount.map((product, index) => ({
                    value: Number(product.amount ?? 0),
                    itemStyle: {color: ChannelMixColors[index % ChannelMixColors.length]},
                })),
                barMaxWidth: 26,
                barCategoryGap: "28%",
            },
        ],
    };

    const loadPresentation = productProfitabilityLoadPresentation({error: loadError, errorMessage: loadErrorMessage, t});
    const afterMarketingKpiInfo = reportProfitFallback
        ? <><ProfitabilityMetricTooltip metric="profit" partialProfitability={partialProfitability}/><div className="mt-1">{t("reporting_lab.product_profitability.order_scope_help", {defaultValue: "Výsledek zahrnuje známé objednávkové řádky celého vybraného období a filtrů. Vyhledávání produktů ani stránkování tabulky tento souhrn nemění."})}</div>{costCoverageAllocationHelp && <div className="mt-1">{costCoverageAllocationHelp}</div>}</>
        : <>{t("reporting_lab.table.contribution_help", {defaultValue: "Hrubý zisk po reklamě = hrubý zisk minus náklady na reklamní média. Jde o částku před dalšími variabilními náklady a režií."})}{afterMarketingKpiPresentation.detail && <div className="mt-1">{afterMarketingKpiPresentation.detail}</div>}{costCoverageAllocationHelp && <div className="mt-1">{costCoverageAllocationHelp}</div>}</>;
    const poasKpiInfo = reportPoasFallback
        ? <><ProfitabilityMetricTooltip metric="poas" partialProfitability={partialProfitability}/><div className="mt-1">{t("reporting_lab.product_profitability.order_scope_help", {defaultValue: "Výsledek zahrnuje známé objednávkové řádky celého vybraného období a filtrů. Vyhledávání produktů ani stránkování tabulky tento souhrn nemění."})}</div>{costCoverageAllocationHelp && <div className="mt-1">{costCoverageAllocationHelp}</div>}</>
        : <>{t("reporting_lab.product_profitability.poas_meta", {defaultValue: "Hrubý zisk / alokované náklady na reklamu"})}{poasKpiPresentation.detail && <div className="mt-1">{poasKpiPresentation.detail}</div>}{partialProfitability?.quality === "estimated" && <div className="mt-1">{t("reporting_lab.partial_profitability.estimate_help", {defaultValue: "Výsledek zahrnuje odhadované nákupní ceny, nikoli výhradně doložené historické ocenění."})}</div>}{costCoverageAllocationHelp && <div className="mt-1">{costCoverageAllocationHelp}</div>} </>;

    return <WideSecureLayout periodReport retentionExpired={retentionExpired}
        title={t("reporting_lab.pages.product_profitability", {defaultValue: "Produktová ziskovost"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        alerts={[loadPresentation.alert]}
     intro={<ReportingPageDescription
                className="col-span-12"
                title={t("reporting_lab.product_profitability.explanation_title", {defaultValue: "K čemu slouží produktová ziskovost"})}
            >
                {t("reporting_lab.product_profitability.explanation", {defaultValue: "Přehled ukazuje ziskovost produktů v dokončených objednávkách. Základ je produktový obrat minus nákupní cena zboží; marketingový spend se na produkty alokuje poměrně podle jejich podílu na obratu. Doprava, platby a ostatní režijní náklady zatím nejsou do produktu alokované."})}
            </ReportingPageDescription>}>
        {loadPresentation.showReport && <div className="grid grid-cols-12 gap-4">
            <ReportingSnapshotStatus snapshots={productPublication}/>
            <ReportingCostCoverageContext
                data={partialProfitability}
                reportContext="product_profitability"
                productMode="net"
                loading={loading}
                requestEnabled={!loading && purchaseCoverageRange !== null}
                requestFilters={purchaseCoverageRange ? {...purchaseCoverageRange, selectedCentreIds: centresAllowListed, selectedDomainIds: domainsAllowListed} : undefined}
            />
            {data && marketingMetricsUnavailable && <div role="status" className="app-surface col-span-12 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                {costCoverageAllocationHelp}
            </div>}

            {showProfitCard && <div className={productProfitabilityKpiGridItemClass}>
                <KpiCard loading={loading} label={reportProfitSelection.partial ? t("reporting_lab.kpi.partial_profit", {defaultValue: "Dílčí hrubý zisk po reklamě"}) : t("reporting_lab.product_profitability.profit_after_marketing", {defaultValue: "Hrubý zisk po reklamě"})} value={hasProfit || reportProfitFallback ? renderCurrencyValue(hasProfit ? summary.profitAfterMarketing : reportProfitSelection.value, true) : "—"} valueClassName={!hasProfit && !reportProfitFallback && centreSpendStatus !== null ? "text-slate-500 dark:text-slate-400" : ""} meta={reportProfitFallback ? undefined : afterMarketingKpiPresentation.meta} info={afterMarketingKpiInfo} infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})} tone={(hasProfit || reportProfitFallback) && totalProfitAfterMarketing < 0 ? "bad" : "default"}/>
            </div>}
            {hasGrossProfitMetric && <div className={productProfitabilityKpiGridItemClass}>
                <KpiCard loading={loading} label={grossProfitSelection.partial ? t("reporting_lab.kpi.partial_gross_profit", {defaultValue: "Dílčí hrubý zisk"}) : t("reporting_lab.product_profitability.margin_before_marketing", {defaultValue: "Hrubý zisk"})} value={renderCurrencyValue(grossProfitSelection.value, true)} meta={`${t("reporting_lab.kpi.revenue", {defaultValue: "Obrat"})}: ${renderCurrencyValue({amount: totalTurnover, currency}, true)}`} info={<>{t("reporting_lab.product_profitability.partial_gross_profit_help", {defaultValue: "Hrubý zisk zahrnuje jen produktové řádky se známou tržbou a doloženým nákupním nákladem. Reklamní náklady v této částce nejsou odečteny."})}{partialProfitability?.quality === "estimated" && <div className="mt-1">{t("reporting_lab.partial_profitability.estimate_help", {defaultValue: "Výsledek zahrnuje odhadované nákupní ceny, nikoli výhradně doložené historické ocenění."})}</div>}</>} infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}/>
            </div>}
            {hasPurchaseCostMetric && <div className={productProfitabilityKpiGridItemClass}>
                <KpiCard loading={loading} label={purchaseCostSelection.partial ? t("reporting_lab.table.purchase", {defaultValue: "Nákup"}) : t("reporting_lab.table.purchase", {defaultValue: "Nákupní náklady"})} value={renderCurrencyValue(purchaseCostSelection.value, true)} meta={purchaseCostSelection.partial ? t("reporting_lab.product_profitability.known_purchase_prices", {defaultValue: "Známé nákupní ceny"}) : t("reporting_lab.products.completed_orders_basis", {defaultValue: "Dokončené objednávky"})} info={<>{t("reporting_lab.product_profitability.purchase_coverage_info", {defaultValue: "Nákupní náklady zahrnují jen produktové řádky s doloženým použitelným nákladem."})}{partialProfitability?.quality === "estimated" && <div className="mt-1">{t("reporting_lab.partial_profitability.estimate_help", {defaultValue: "Výsledek zahrnuje odhadované nákupní ceny, nikoli výhradně doložené historické ocenění."})}</div>}</>} infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}/>
            </div>}
            {showMarketingAllocationCard && <div className={productProfitabilityKpiGridItemClass}>
                <KpiCard
                    loading={loading}
                    label={t("reporting_lab.product_profitability.allocated_marketing", {defaultValue: "Alokovaný marketing"})}
                    value={adMetricAvailability.showKnownAllocation ? renderCurrencyValue(summary.allocatedMarketingSpend, true) : "—"}
                    valueClassName={!hasMarketingAllocation && centreSpendStatus !== null ? "text-slate-500 dark:text-slate-400" : ""}
                    meta={hasPurchasePrice
                        ? `${t("reporting_lab.table.purchase", {defaultValue: "Nákup"})}: ${renderCurrencyValue(summary.purchasePrice, true)}`
                        : undefined}
                    info={<>{centreSpendStatus !== null && <div>{productCentreSpendStatusHelp(centreSpendStatus, t)}</div>}{centreSpendStatus === null && hasPurchasePrice && !costComplete && t("reporting_lab.cost_quality.known_cost_subtotal", {defaultValue: "Známý mezisoučet nákupních cen, ne celkové COGS"})}</>}
                    infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}
                />
            </div>}
            {((hasPurchasePrice && adMetricAvailability.poasFieldsReturned) || reportPoasFallback) && <div className={productProfitabilityKpiGridItemClass}>
                <KpiCard loading={loading} label="POAS" value={formatChartMultiplier(productPoas ?? reportPoasSelection.value)} valueClassName={adMetricAvailability.showUnavailablePoas && !reportPoasFallback ? "text-slate-500 dark:text-slate-400" : ""} meta={reportPoasFallback ? undefined : poasKpiPresentation.meta} info={poasKpiInfo} infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}/>
            </div>}
            {purchaseCoveragePercent !== null && <div className={productProfitabilityKpiGridItemClass}>
                <KpiCard
                    loading={loading}
                    label={t("reporting_lab.product_profitability.purchase_coverage", {defaultValue: "Pokrytí nákupních cen"})}
                    value={formatProductPurchaseCoveragePercent(purchaseCoveragePercent)}
                    info={[
                        t("reporting_lab.product_profitability.purchase_coverage_info", {defaultValue: "Úplnost nákupních podkladů se posuzuje na produktových řádcích zahrnutých dokončených objednávek. Počet produktových identit v katalogu není počtem prodaných řádků ani mírou pokrytí nákladů."}),
                        actionablePurchaseCostCounts.lines > 0 ? t("reporting_lab.partial_profitability.actionable_lines", {count: formatCzechProductLineCount(actionablePurchaseCostCounts.lines, renderNumber), defaultValue: "K doplnění nákupní ceny: {{count}}."}) : null,
                        purchaseCoveragePresentation.unresolvedOrders > 0 ? t("reporting_lab.cost_quality.purchase_cost_unresolved_orders", {count: purchaseCoveragePresentation.unresolvedOrders, countText: renderNumber(purchaseCoveragePresentation.unresolvedOrders)}) : null,
                        purchaseCoveragePresentation.estimatedRows > 0 ? t("reporting_lab.cost_quality.estimated_rows", {countText: renderNumber(purchaseCoveragePresentation.estimatedRows)}) : null,
                    ].filter(Boolean).join(" ")}
                    infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}
                />
            </div>}

            {data && (hasExcludedNonProductAmounts || hasUnallocatedAdjustmentNotice) && <div className="app-surface col-span-12 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm dark:border-slate-700 dark:bg-slate-800">
                {hasExcludedNonProductAmounts && <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-semibold text-slate-800 dark:text-slate-100">{t("reporting_lab.product_profitability.excluded_non_product_amount", {defaultValue: "Nezbožní částky s neurčeným účelem"})}</span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">{excludedNonProductAmount != null ? renderCurrencyValue(excludedNonProductAmount, true) : "—"}</span>
                    <span className="basis-full text-slate-500 dark:text-slate-400">{t("reporting_lab.product_profitability.excluded_non_product_amount_help", {
                        defaultValue: "Počet dotčených objednávek: {{countText}}. Částka není přiřazena k produktovým řádkům a její účel nelze určit; nezahrnuje se do produktové ziskovosti.",
                        countText: renderNumber(excludedNonProductOrderCount),
                    })}</span>
                </div>}
                {hasUnallocatedAdjustmentNotice && <div className={`${hasExcludedNonProductAmounts ? "mt-2 border-t border-slate-100 pt-2 dark:border-slate-700" : ""} flex items-center justify-between gap-2`}>
                    <span className="inline-flex items-center gap-1.5 font-semibold text-slate-800 dark:text-slate-100"><span>{t("reporting_lab.product_profitability.unallocated_adjustments", {defaultValue: "Nepřiřazené úpravy a rozdíly"})}</span><ReportingInfoTooltip ariaLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}>
                        <p>
                        {t("reporting_lab.product_profitability.unallocated_adjustments_help", {defaultValue: "Rozdíl mezi částkou za zboží v objednávkách a součtem produktových řádků; může zahrnovat slevy bez produktu, chybějící identifikaci nebo zaokrouhlení."})}
                        {!adjustmentComplete && ` ${t("reporting_lab.product_profitability.unallocated_adjustments_incomplete", {defaultValue: "Úplnou hodnotu se nepodařilo ověřit."})}`}
                        {Number(summary.unallocatedAdjustmentUnknownOrderCount ?? 0) > 0 && ` ${t("reporting_lab.product_profitability.adjustment_unknown_orders", {defaultValue: "Neověřené objednávky: {{countText}}.", countText: renderNumber(summary.unallocatedAdjustmentUnknownOrderCount)})}`}
                        {tableSearch.trim() && ` ${t("reporting_lab.product_profitability.adjustments_ignore_search", {defaultValue: "Hledání produktu nemění rozsah této hodnoty."})}`}
                        </p>
                    </ReportingInfoTooltip></span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">{availableUnallocatedAdjustments != null ? renderCurrencyValue(availableUnallocatedAdjustments, true) : "—"}</span>
                </div>}
            </div>}

            {hasProfit && <Panel
                loading={loading}
                title={t("reporting_lab.product_profitability.profit_by_product", {defaultValue: "Hrubý zisk po reklamě podle produktu"})}
                className="col-span-12 flex flex-col xl:col-span-8"
                contentClassName="flex flex-1 items-center justify-center"
            >
                <EChart option={profitByProductOption} className="h-[28rem]"/>
            </Panel>}
            {hasProfit && <Panel loading={loading} title={t("reporting_lab.product_profitability.top_profit_share", {defaultValue: "Koncentrace hrubého zisku po reklamě"})} className="col-span-12 xl:col-span-4">
                <EChart option={profitByChannelOption} className="h-96"/>
            </Panel>}
            {!productDataIncomplete && grossProfitProducts.length > 0 && <Panel
                loading={loading}
                title={t("reporting_lab.product_profitability.gross_profit_by_product", {defaultValue: "Hrubý zisk podle produktu (top produkty podle kusů)"})}
                className="col-span-12"
            >
                <EChart option={grossProfitByProductOption} className="h-[28rem]"/>
            </Panel>}

            <div className="col-span-12 grid grid-cols-12 gap-4">
            {!productDataIncomplete && <Panel loading={loading} title={t("reporting_lab.product_profitability.amount_by_product", {defaultValue: "Top 20 produktů podle počtu kusů"})} className={`col-span-12 ${tableExpanded ? "order-2 xl:col-span-12" : "order-1 xl:col-span-5"}`}>
                <EChart option={quantityByProductOption} height={quantityChartHeight}/>
            </Panel>}
            <Panel
                loading={loading}
                title={<ExpandablePanelTitle
                    title={t("reporting_lab.product_profitability.product_table", {defaultValue: "Produktový přehled"})}
                    expanded={tableExpanded}
                    onToggle={() => setTableExpanded((expanded) => !expanded)}
                    expandLabel={t("reporting_lab.table.expand", {defaultValue: "Rozšířit"})}
                    collapseLabel={t("reporting_lab.table.collapse", {defaultValue: "Zúžit"})}
                />}
                className={`col-span-12 ${tableExpanded ? "order-1 xl:col-span-12" : "order-2 xl:col-span-7"}`}
            >
                <TableSearch value={tableSearch} onChange={(value) => { setTableSearch(value); setTablePage(1); }} visible={!loading && sourceTotalItems > 15} t={t}/>
                <div className="overflow-x-auto">
                    <table className="min-w-[80rem] table-fixed divide-y divide-slate-100 text-sm dark:divide-slate-700">
                        <thead>
                        <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                            <SortableHeader label={t("reporting_lab.products.product", {defaultValue: "Produkt"})} sortKey="product" sort={tableSort} onSort={toggleSort}/>
                            <SortableHeader label={t("reporting_lab.products.quantity", {defaultValue: "Kusy"})} sortKey="amount" sort={tableSort} onSort={toggleSort} align="right" className="w-20"/>
                            <SortableHeader label={t("reporting_lab.table.revenue", {defaultValue: "Obrat"})} sortKey="turnover" sort={tableSort} onSort={toggleSort} align="right" className="w-32"/>
                            {hasPurchasePrice && <SortableHeader label={t("reporting_lab.table.purchase", {defaultValue: "Nákup"})} tooltip={partialCostTooltipColumn === "purchasePrice" ? t("reporting_lab.product_profitability.row_partial_cost_help", {defaultValue: "U produktů s neúplnými nákupními podklady jsou nákupní náklady a hrubý zisk mezisoučtem řádků s dostupnou cenou. Mohou zahrnovat odhadované ceny; jejich kvalita je uvedena v pokrytí nákupních podkladů. Obrat produktu zůstává úplný."}) : null} sortKey="purchasePrice" sort={tableSort} onSort={toggleSort} align="right" className="w-32"/>}
                            {profitabilityColumns.grossMargin && <SortableHeader label={t("reporting_lab.product_profitability.margin_before_marketing_short", {defaultValue: "Hrubý zisk"})} tooltip={partialCostTooltipColumn === "grossMargin" ? t("reporting_lab.product_profitability.row_partial_cost_help", {defaultValue: "U produktů s neúplnými nákupními podklady jsou nákupní náklady a hrubý zisk mezisoučtem řádků s dostupnou cenou. Mohou zahrnovat odhadované ceny; jejich kvalita je uvedena v pokrytí nákupních podkladů. Obrat produktu zůstává úplný."}) : null} sortKey="grossMargin" sort={tableSort} onSort={toggleSort} align="right" className="w-32"/>}
                            <SortableHeader label={t("reporting_lab.product_profitability.allocated_marketing_short", {defaultValue: "Marketing"})} tooltip={centreSpendStatus !== null ? productCentreSpendStatusHelp(centreSpendStatus, t) : null} sortKey="allocatedMarketingSpend" sort={tableSort} onSort={toggleSort} align="right" className="w-32"/>
                            {profitabilityColumns.afterMarketing && <SortableHeader label={t("reporting_lab.table.contribution", {defaultValue: "Hrubý zisk po reklamě"})} tooltip={<>{t("reporting_lab.table.contribution_help", {defaultValue: "Hrubý zisk po reklamě = hrubý zisk minus náklady na reklamní média. Jde o částku před dalšími variabilními náklady a režií; u produktů jsou náklady na reklamu analyticky alokované."})}{centreSpendStatus !== null && <div className="mt-1">{productCentreSpendStatusHelp(centreSpendStatus, t)}</div>}</>} sortKey="profitAfterMarketing" sort={tableSort} onSort={toggleSort} align="right" className="w-40 pr-4"/>}
                            {profitabilityColumns.afterMarketing && <SortableHeader label={t("reporting_lab.product_profitability.margin_after_marketing", {defaultValue: "Marže po reklamě (%)"})} tooltip={centreSpendStatus !== null ? productCentreSpendStatusHelp(centreSpendStatus, t) : null} sortKey="marginPercent" sort={tableSort} onSort={toggleSort} align="right" className="w-32"/>}
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {visibleProducts.map((product) => {
                            const rowKnownCostMetrics = productProfitabilityRowKnownCostMetrics(product);
                            const productProfitAvailable = productProfitabilityRowAvailable(product);
                            return <tr key={`${product.integrationId ?? "integration"}-${product.productRemoteId ?? product.productName}`}>
                                <td className="max-w-[30rem] py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">
                                    <div className="truncate">{product.productName}</div>
                                </td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(product.amount)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(product.turnover, true)}</td>
                                {hasPurchasePrice && <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{rowKnownCostMetrics.purchaseCost.value != null ? renderCurrencyValue(rowKnownCostMetrics.purchaseCost.value, true) : "—"}</td>}
                                {profitabilityColumns.grossMargin && <ProductGrossMarginCell product={product} renderCurrencyValue={renderCurrencyValue}/>}
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{productRowMarketingAllocationDisplayAvailable(baseProfitabilityPublication, summary, product, centreSpendStatus)
                                    ? renderCurrencyValue(product.allocatedMarketingSpend, true)
                                    : "—"}</td>
                                {profitabilityColumns.afterMarketing && <td className={`py-3 pr-4 text-right ${summary.marketingAllocationComplete !== false && productProfitAvailable && moneyAmount(product.profitAfterMarketing) < 0 ? "text-datixo-negative" : "text-slate-600 dark:text-slate-300"}`}>{summary.marketingAllocationComplete !== false && productProfitAvailable ? renderCurrencyValue(product.profitAfterMarketing, true) : "—"}</td>}
                                {profitabilityColumns.afterMarketing && <td className="py-3 text-right text-slate-600 dark:text-slate-300">{summary.marketingAllocationComplete !== false && productProfitAvailable ? renderPercentValue(dividePercent(moneyAmount(product.profitAfterMarketing), moneyAmount(product.turnover))) : "—"}</td>}
                            </tr>;
                        })}
                        </tbody>
                    </table>
                </div>
                {tablePresentation.showPagination && <TablePagination page={normalizedTablePage} pageSize={tablePageSize} totalItems={matchingItems} onPageChange={setTablePage} onPageSizeChange={(value) => { setTablePageSize(value); setTablePage(1); }} t={t}/>}
                {tablePresentation.showScopeSummary && <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
                    {isTruncated
                        ? t("reporting_lab.product_profitability.scope_summary_truncated", {
                            defaultValue: "Zdroj: {{source}} produktů · odpovídá filtrům: {{matching}} · načteno na této stránce: {{loaded}}",
                            source: renderNumber(sourceTotalItems),
                            matching: renderNumber(matchingItems),
                            loaded: renderNumber(loadedItems),
                        })
                        : t("reporting_lab.product_profitability.scope_summary_complete", {
                            defaultValue: "Zdroj: {{source}} produktů · odpovídá filtrům: {{matching}}",
                            source: renderNumber(sourceTotalItems),
                            matching: renderNumber(matchingItems),
                        })}
                </div>}
                {tablePresentation.emptyState && <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">{tablePresentation.emptyState === "unavailable"
                    ? t("reporting_lab.snapshot.no_published_days_short", {defaultValue: "Bez publikovaných dnů"})
                    : tablePresentation.emptyState === "publishedSubsetEmpty"
                        ? t("reporting_lab.snapshot.partial_published_days_short", {defaultValue: "Dílčí data jen za publikované dny"})
                        : tablePresentation.emptyState === "noMatchingProducts"
                    ? t("reporting_lab.product_profitability.no_eligible_products", {defaultValue: "Žádné produkty neodpovídají aktuálnímu hledání a filtrům."})
                    : t("reporting_lab.products.empty", {defaultValue: "Pro vybrané období nejsou dostupná produktová data."})}</div>}
            </Panel>
            </div>
        </div>}
    </WideSecureLayout>;
}
